import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { DateTimePickerDE } from "@/shared/data-entries/common/DateTimePickerDE";
import {
  ErrorModal,
  MesfModal,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, Grid, TextField } from "@mui/material";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { get, isNil } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { modalStyles } from "@/shared/styles/modalStyles";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useLMFDataEntryActions } from "../../../hooks/useLMFDataEntryActions";
import { Probe } from "../../../models/lmf.models";
import { validateTemperature, validateTimestamp } from "@/utils/validations";

type Props = {
  showModal: "NEW" | "EDIT" | "";
  onHide: () => void;
  probe: Probe | undefined;
};

export const ProbeModal = ({ showModal, onHide, probe }: Props) => {
  const { state } = useUTLSettingsContext();
  const maxTempManualProbe = parseInt(
    get(state.settings, "LMF_DE_PROBES_MAX_TEMP", 3200),
  );
  const minTempManualProbe = parseInt(
    get(state.settings, "LMF_DE_PROBES_MIN_TEMP", 2500),
  );
  const maxO2Probe = parseInt(
    get(state.settings, "LMF_DE_PROBES_MAX_O2", 2000),
  );

  const { saveProbe } = useLMFDataEntryActions();
  const {
    state: { deProcess },
  } = useDataEntryContext();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState<string | null>(
    null,
  );

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Probe>({});

  const onSubmit: SubmitHandler<any> = async (data: any) => {
    if (data.Oxygen > maxO2Probe) {
      setPendingData(data);
      setShowWarningModal(true);
      return;
    }
    setIsSubmitLoading(true);
    await saveProbe(data);
    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (showModal !== "") {
      if (showModal === "NEW") {
        reset({ Timestamp: deProcess?.process.Start });
      } else if (probe) {
        const formattedTemp = !isNil(probe.Temperature)
          ? parseFloat(probe.Temperature.toFixed(0))
          : null;
        const formattedOxygen = !isNil(probe.Oxygen)
          ? parseFloat(probe.Oxygen.toFixed(1))
          : null;
        const formattedCarbon = !isNil(probe.Carbon)
          ? parseFloat(probe.Carbon.toFixed(3))
          : null;
        reset({
          ...probe,
          Oxygen: formattedOxygen,
          Carbon: formattedCarbon,
          Temperature: formattedTemp,
        });
      }
    }
  }, [showModal, deProcess?.process.Start, probe, reset]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            title={`${showModal} MANUAL PROBE`}
            open={showModal !== ""}
            handleClose={() => onHide()}
            id="probe-modal"
            maxWidth="sm"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content dividers sx={modalStyles.modalContent}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="Timestamp"
                      control={control}
                      defaultValue={probe?.Timestamp}
                      rules={{
                        required: "Timestamp is required",
                        validate: (value) =>
                          validateTimestamp(value, deProcess?.process.Start),
                      }}
                      render={({ field }) => (
                        <DateTimePickerDE
                          {...field}
                          format={"HH:mm"}
                          disableOpenPicker
                          label="Time"
                          fieldError={errors.Timestamp}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="Temperature"
                      control={control}
                      rules={{
                        required: "Temperature is required",
                        validate: (value) =>
                          validateTemperature(
                            value,
                            minTempManualProbe,
                            maxTempManualProbe,
                          ),
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Temperature"
                          error={!!error}
                          helperText={error?.message}
                          inputProps={{
                            min: 0,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="Oxygen"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Oxygen"
                          error={!!error}
                          helperText={error?.message}
                          inputProps={{
                            step: 0.1,
                            min: 0.0,
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="Carbon"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          type="number"
                          variant="outlined"
                          fullWidth
                          label="Carbon"
                          error={!!error}
                          helperText={error?.message}
                          inputProps={{
                            step: 0.001,
                            min: 0.0,
                            max: 1,
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </MesfModal.Content>
              <MesfModal.Actions sx={modalStyles.modalContent}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
          <NotificationSnackBar
            message={notificationMessage}
            onHide={() => setNotificationMessage(null)}
            severity="success"
          />
        </Grid>
      </Grid>
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error Editing Manual Probe"
      />
      <MesfModal
        open={showWarningModal}
        handleClose={() => setShowWarningModal(false)}
        title="Warning"
        id="warningModal"
      >
        <MesfModal.Content>
          <p>O₂ value is above ({maxO2Probe}). Do you want to proceed?</p>
        </MesfModal.Content>
        <MesfModal.Actions>
          <Button
            fullWidth
            variant="contained"
            color="inherit"
            onClick={() => setShowWarningModal(false)}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={async () => {
              setShowWarningModal(false);
              if (pendingData) {
                setIsSubmitLoading(true);
                await saveProbe(pendingData);
                setIsSubmitLoading(false);
                onHide();
                setPendingData(null);
              }
            }}
          >
            Proceed
          </Button>
        </MesfModal.Actions>
      </MesfModal>
    </>
  );
};
